/* =====================================================================
   UCHI — Homepage v3 behaviour
   - Add to cart (one-tap, with optional selling plan) → theme cart drawer
   - Shop rail: audience pills + progress line
   - System: auto-advancing steps (click / keyboard to jump)
   - Refills: plan picker updates the button
   - UGC: videos play in view (desktop) / on tap (mobile)
   Re-initialises on Shopify theme-editor section reloads.
   ===================================================================== */
import { CartAddEvent } from '@theme/events';

const mqDesktop = window.matchMedia('(min-width: 750px)');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ---------- Add to cart ------------------------------------------------ */
async function addToCart(button) {
  const variantId = Number(button.dataset.variant);
  if (!variantId || button.getAttribute('aria-busy') === 'true') return;
  const item = { id: variantId, quantity: 1 };
  if (button.dataset.plan) item.selling_plan = Number(button.dataset.plan);

  const label = button.querySelector('[data-u3-label]') || button;
  const original = label.textContent;
  button.setAttribute('aria-busy', 'true');

  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    if (!res.ok) throw new Error((await res.json()).description || 'Add to cart failed');
    const cart = await (await fetch(`${root}cart.js`)).json();

    document.dispatchEvent(
      new CartAddEvent({}, 'uchi-v3', {
        source: 'uchi-v3',
        itemCount: cart.item_count,
        variantId: String(variantId),
      })
    );
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('auto-open')) drawer.open();
    if (!drawer) window.location.href = `${root}cart`;

    if (button.dataset.added) {
      label.textContent = button.dataset.added;
      setTimeout(() => { label.textContent = original; }, 2400);
    }
  } catch (err) {
    console.error('[uchi-v3]', err);
    label.textContent = button.dataset.error || original;
    setTimeout(() => { label.textContent = original; }, 2400);
  } finally {
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-u3-add]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  addToCart(btn);
});

/* ---------- Shop rail -------------------------------------------------- */
function initShop(section) {
  const rail = section.querySelector('[data-u3-rail]');
  const bar = section.querySelector('[data-u3-progress]');
  const pills = [...section.querySelectorAll('[data-u3-pill]')];
  if (!rail) return;

  const onScroll = () => {
    if (!bar) return;
    const max = rail.scrollWidth - rail.clientWidth;
    const p = max > 0 ? rail.scrollLeft / max : 0;
    bar.style.left = `${(p * 75).toFixed(1)}%`;
    if (!mqDesktop.matches && pills.length === 2) {
      setPressed(p > 0.85 ? 1 : 0);
    }
  };
  const setPressed = (i) => pills.forEach((p, j) => p.setAttribute('aria-pressed', String(i === j)));

  rail.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  pills.forEach((pill, i) => {
    pill.addEventListener('click', () => {
      const aud = pill.dataset.u3Pill;
      setPressed(i);
      const cards = [...rail.querySelectorAll('[data-aud]')];
      if (mqDesktop.matches) {
        cards.forEach((c) => {
          const a = c.dataset.aud;
          c.classList.toggle('is-dim', i !== 0 && a !== 'all' && a !== aud);
        });
      } else {
        cards.forEach((c) => c.classList.remove('is-dim'));
        const target = i === 0 ? cards[0] : cards.find((c) => c.dataset.aud === aud) || cards[cards.length - 1];
        if (target) rail.scrollTo({ left: target.offsetLeft - rail.offsetLeft - parseFloat(getComputedStyle(rail).paddingLeft), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    });
  });
}

/* ---------- System steps ----------------------------------------------- */
function initSystem(section) {
  const slides = [...section.querySelectorAll('[data-u3-slide]')];
  const tabs = [...section.querySelectorAll('[data-u3-step]')];
  const descs = [...section.querySelectorAll('[data-u3-desc]')];
  const led = section.querySelector('[data-u3-led]');
  const n = Number(section.dataset.steps || descs.length || 0);
  if (!n) return;
  let index = 0;
  let timer = null;
  let inView = false;
  let hover = false;
  const interval = Number(section.dataset.interval || 3200);
  const autoplay = section.dataset.autoplay === 'true' && !reduceMotion.matches;

  const show = (i) => {
    index = (i + n) % n;
    slides.forEach((s) => s.classList.toggle('is-active', Number(s.dataset.i) === index));
    tabs.forEach((t) => t.setAttribute('aria-selected', String(Number(t.dataset.u3Step) === index)));
    descs.forEach((d, j) => (d.hidden = j !== index));
    const current = slides.find((s) => Number(s.dataset.i) === index);
    if (led) led.textContent = (current && current.dataset.led) || '';
    slides.forEach((s) => {
      const v = s.querySelector('video');
      if (!v) return;
      const visible = s.offsetParent !== null || s.getClientRects().length > 0;
      if (Number(s.dataset.i) === index && visible) { v.preload = 'auto'; v.play && v.play().catch(() => {}); }
      else v.pause && v.pause();
    });
  };
  const stop = () => { clearInterval(timer); timer = null; };
  const start = () => {
    stop();
    if (autoplay && inView && !hover && n > 1) timer = setInterval(() => show(index + 1), interval);
  };

  tabs.forEach((t) => {
    t.addEventListener('click', () => { show(Number(t.dataset.u3Step)); start(); });
    t.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
      show(index + dir);
      const same = tabs.filter((x) => x.closest('[role="tablist"]') === t.closest('[role="tablist"]'));
      same.find((x) => Number(x.dataset.u3Step) === index)?.focus();
      start();
    });
  });
  section.querySelectorAll('[role="tablist"]').forEach((list) => {
    list.addEventListener('pointerenter', (e) => { if (e.pointerType !== 'mouse') return; hover = true; stop(); });
    list.addEventListener('pointerleave', (e) => { if (e.pointerType !== 'mouse') return; hover = false; start(); });
  });
  new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; inView ? start() : stop(); }, { threshold: 0.2 }).observe(section);
  show(0);
}

/* ---------- Refill plans ----------------------------------------------- */
function initPlans(section) {
  const btn = section.querySelector('[data-u3-plan-cta]');
  const inputs = [...section.querySelectorAll('input[name^="u3-plan"]')];
  if (!btn || !inputs.length) return;
  const label = btn.querySelector('[data-u3-label]');
  const apply = () => {
    const current = inputs.find((i) => i.checked) || inputs[0];
    btn.dataset.plan = current.dataset.plan || '';
    if (!current.dataset.plan) delete btn.dataset.plan;
    if (label) label.textContent = current.dataset.cta;
  };
  inputs.forEach((i) => i.addEventListener('change', apply));
  apply();
}

/* ---------- UGC videos ------------------------------------------------- */
function initUgc(section) {
  const videos = [...section.querySelectorAll('video[data-u3-ugc]')];
  if (!videos.length) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!mqDesktop.matches || reduceMotion.matches) return;
      if (isIntersecting) {
        if (target.preload === 'none') target.preload = 'metadata';
        target.play().catch(() => {});
      } else target.pause();
    });
  }, { threshold: 0.5 });
  videos.forEach((v) => io.observe(v));
  section.querySelectorAll('[data-u3-play]').forEach((b) => {
    b.addEventListener('click', () => {
      const v = b.parentElement.querySelector('video');
      if (!v) return;
      if (v.paused) { v.play().catch(() => {}); b.setAttribute('aria-pressed', 'true'); }
      else { v.pause(); b.setAttribute('aria-pressed', 'false'); }
    });
  });
}

/* ---------- Hero video: respect reduced motion ------------------------- */
function initHero(section) {
  const v = section.querySelector('video');
  if (!v) return;
  if (reduceMotion.matches) { v.removeAttribute('autoplay'); v.pause(); return; }
  v.muted = true;
  v.play && v.play().catch(() => {});
}

/* ---------- Desktop-only background videos ---------------------------- */
function initSense(section) {
  const v = section.querySelector('video[data-u3-dvideo]');
  if (!v || reduceMotion.matches) return;
  new IntersectionObserver(([entry]) => {
    if (!mqDesktop.matches) return;
    if (entry.isIntersecting) { v.preload = 'auto'; v.play().catch(() => {}); } else v.pause();
  }, { threshold: 0.2 }).observe(section);
}

/* ---------- Sticky buy bar ------------------------------------------- */
function initBuybar(bar) {
  const anchor = document.getElementById(bar.dataset.anchor || 'start');
  const hiders = [...document.querySelectorAll('[data-u3-buybar-hide], footer, .shopify-section-group-footer-group')];
  let pastAnchor = false;
  const hidden = new Set();
  const update = () => {
    const show = pastAnchor && hidden.size === 0;
    bar.classList.toggle('is-visible', show);
    bar.setAttribute('aria-hidden', String(!show));
    bar.toggleAttribute('inert', !show);
  };
  if (anchor) {
    new IntersectionObserver(([e]) => {
      // Visible only once the anchor has scrolled up and out of view (not before reaching it).
      pastAnchor = !e.isIntersecting && e.boundingClientRect.top < 0;
      update();
    }).observe(anchor);
  } else {
    pastAnchor = true;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => (e.isIntersecting ? hidden.add(e.target) : hidden.delete(e.target)));
    update();
  });
  hiders.forEach((h) => io.observe(h));
  update();

  // v4: swap to the subscription offer while the Refills section is on screen.
  const swapTarget = bar.dataset.swap ? document.querySelector(bar.dataset.swap) : null;
  if (swapTarget) {
    new IntersectionObserver(([e]) => {
      bar.classList.toggle('is-alt', e.isIntersecting);
    }, { rootMargin: '-25% 0px -25% 0px' }).observe(swapTarget);
  }
}

/* ---------- Boot ------------------------------------------------------- */
const inits = { buybar: initBuybar, sense: initSense, shop: initShop, system: initSystem, plans: initPlans, ugc: initUgc, hero: initHero };
function boot(scope = document) {
  scope.querySelectorAll('[data-u3]').forEach((el) => {
    if (el.__u3) return;
    el.__u3 = true;
    const fn = inits[el.dataset.u3];
    if (fn) fn(el);
  });
}
boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
